// ── SupplierSpendTab.js ───────────────────────────────────────────────────────
import { useState, useEffect, useCallback, useRef } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Search, Filter, X, ChevronDown } from 'lucide-react';
import { supabase } from '../supabaseClient';
import {
  C, SUPABASE_FUNCTIONS_URL,
  fmt, fmtFull, parseXeroDate, fmtDate, mKey, getLast12Months,
  isSupplierHidden, useIsMobile,
  Spinner, ErrorBanner, StatRow, PillToggle, CustomTooltip, StatusPill,
} from './financeUtils';

// ── Invoice detail modal ──────────────────────────────────────────────────────
function InvoiceModal({ inv, onClose }) {
  if (!inv) return null;
  const codes = [...new Set((inv.LineItems || []).map(l => l.AccountCode || 'Unassigned'))].join(', ');
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', zIndex: 1000, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}
      onClick={onClose}>
      <div style={{ background: C.white, borderRadius: '16px 16px 0 0', width: '100%', maxWidth: 540, padding: '1.5rem', paddingBottom: 'calc(1.5rem + env(safe-area-inset-bottom))' }}
        onClick={e => e.stopPropagation()}>
        <div style={{ width: 36, height: 4, borderRadius: 2, background: C.borderMid, margin: '0 auto 1.25rem' }} />
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.25rem' }}>
          <div>
            <div style={{ fontWeight: 800, fontSize: '1rem', color: C.ink }}>{inv.InvoiceNumber || inv.Reference || 'Invoice'}</div>
            <div style={{ fontSize: '0.8rem', color: C.mid, marginTop: 2 }}>{fmtDate(inv.Date)}</div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <StatusPill status={inv.Status} dueDate={inv.DueDate} invoiceType={inv.Type} />
            <button onClick={onClose} style={{ border: 'none', background: C.rowAlt, borderRadius: 8, padding: '0.35rem', cursor: 'pointer', display: 'flex', alignItems: 'center' }}><X size={16} color={C.mid} /></button>
          </div>
        </div>
        {[
          { label: 'Amount ex VAT',  value: fmtFull(parseFloat(inv.SubTotal || 0)) },
          { label: 'Amount inc VAT', value: fmtFull(parseFloat(inv.AmountDue || inv.Total || 0)) },
          { label: 'Account Code',   value: codes || '—' },
          { label: 'Due Date',       value: fmtDate(inv.DueDate) },
        ].map(({ label, value }) => (
          <div key={label} style={{ display: 'flex', justifyContent: 'space-between', padding: '0.7rem 0', borderBottom: `1px solid ${C.border}` }}>
            <span style={{ fontSize: '0.83rem', color: C.mid }}>{label}</span>
            <span style={{ fontSize: '0.83rem', fontWeight: 700, color: C.ink }}>{value}</span>
          </div>
        ))}
        {inv.LineItems?.length > 0 && (
          <div style={{ marginTop: '1rem' }}>
            <div style={{ fontSize: '0.72rem', fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.5rem' }}>Line Items</div>
            {inv.LineItems.map((line, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '0.4rem 0', borderBottom: `1px solid ${C.border}`, fontSize: '0.82rem' }}>
                <span style={{ color: C.mid, flex: 1 }}>{line.Description || line.AccountCode || '—'}</span>
                <span style={{ fontWeight: 600, color: C.ink }}>{fmtFull(parseFloat(line.LineAmount || 0))}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Supplier Search ───────────────────────────────────────────────────────────
function SupplierSearch({ suppliers, loading, selected, onSelect }) {
  const [search, setSearch] = useState('');
  const [open, setOpen]     = useState(false);
  const wrapRef             = useRef(null);

  useEffect(() => {
    function h(e) { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); }
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const filtered = suppliers.filter(s => s.Name.toLowerCase().includes(search.toLowerCase()));

  return (
    <div ref={wrapRef} style={{ position: 'relative', maxWidth: 420 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', background: C.white, border: `1.5px solid ${open ? C.navy : C.border}`, borderRadius: 10, padding: '0.65rem 0.9rem', cursor: 'pointer' }}
        onClick={() => setOpen(o => !o)}>
        <Search size={14} color={C.mid} />
        {selected && !open
          ? <span style={{ flex: 1, fontSize: '0.88rem', color: C.ink, fontWeight: 700 }}>{selected.Name}</span>
          : <input autoFocus={open} value={search} onChange={e => { setSearch(e.target.value); setOpen(true); }}
              onClick={e => { e.stopPropagation(); setOpen(true); }}
              placeholder={loading ? 'Loading suppliers…' : 'Search supplier…'}
              style={{ border: 'none', outline: 'none', background: 'transparent', flex: 1, fontSize: '0.88rem', color: C.ink }} />
        }
        {selected
          ? <X size={14} color={C.mid} style={{ cursor: 'pointer', flexShrink: 0 }} onClick={e => { e.stopPropagation(); onSelect(null); setSearch(''); setOpen(false); }} />
          : <ChevronDown size={14} color={C.mid} style={{ flexShrink: 0 }} />
        }
      </div>
      {open && (
        <div style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, zIndex: 200, background: C.white, border: `1px solid ${C.border}`, borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,0.1)', maxHeight: 280, overflowY: 'auto' }}>
          {filtered.length === 0 && <div style={{ padding: '1rem', color: C.mid, fontSize: '0.85rem', textAlign: 'center' }}>No suppliers found</div>}
          {filtered.map(s => (
            <div key={s.ContactID} onMouseDown={() => { onSelect(s); setSearch(''); setOpen(false); }}
              style={{ padding: '0.65rem 1rem', cursor: 'pointer', fontSize: '0.86rem', color: C.ink, borderBottom: `1px solid ${C.border}`, fontWeight: selected?.ContactID === s.ContactID ? 700 : 400 }}
              onMouseEnter={e => e.currentTarget.style.background = C.skyBg}
              onMouseLeave={e => e.currentTarget.style.background = C.white}
            >{s.Name}</div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Due date countdown pill ───────────────────────────────────────────────────
function DuePill({ dueDate }) {
  const due = parseXeroDate(dueDate);
  if (!due) return null;
  const today = new Date(); today.setHours(0,0,0,0);
  const days  = Math.round((due - today) / 86400000);
  const overdue = days < 0;
  const soon    = days >= 0 && days <= 7;
  const bg    = overdue ? C.redBg    : soon ? C.amberBg  : C.greenBg;
  const color = overdue ? C.red      : soon ? C.amber     : C.green;
  const label = overdue ? `${Math.abs(days)}d overdue` : days === 0 ? 'Due today' : `${days}d`;
  return (
    <span style={{ background: bg, color, borderRadius: 5, padding: '0.15rem 0.45rem', fontSize: '0.7rem', fontWeight: 700, whiteSpace: 'nowrap' }}>
      {label}
    </span>
  );
}

// ── Invoice list modal (outstanding / overdue / receivables) ──────────────────
function InvoiceListModal({ title, invoices, filter, total, onSelectInv, onClose }) {
  const filtered = [...invoices]
    .filter(inv => {
      if (filter === 'overdue') {
        const due = parseXeroDate(inv.DueDate);
        const today = new Date(); today.setHours(0,0,0,0);
        return due && due < today;
      }
      return true;
    })
    .sort((a, b) => {
      const da = parseXeroDate(a.DueDate)?.getTime() || Infinity;
      const db = parseXeroDate(b.DueDate)?.getTime() || Infinity;
      return da - db;
    });

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', zIndex: 1000, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}
      onClick={onClose}>
      <div style={{ background: C.white, borderRadius: '16px 16px 0 0', width: '100%', maxWidth: 580, maxHeight: '82vh', display: 'flex', flexDirection: 'column', paddingBottom: 'env(safe-area-inset-bottom)' }}
        onClick={e => e.stopPropagation()}>

        {/* Handle */}
        <div style={{ width: 36, height: 4, borderRadius: 2, background: C.borderMid, margin: '0.85rem auto 0' }} />

        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.85rem 1.1rem 0.65rem', borderBottom: `1px solid ${C.border}`, flexShrink: 0 }}>
          <div>
            <div style={{ fontWeight: 800, fontSize: '0.95rem', color: C.ink }}>{title}</div>
            <div style={{ fontSize: '0.75rem', color: C.mid, marginTop: 2 }}>{filtered.length} invoice{filtered.length !== 1 ? 's' : ''} · {fmt(total)}</div>
          </div>
          <button onClick={onClose} style={{ border: 'none', background: C.rowAlt, borderRadius: 8, padding: '0.35rem', cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
            <X size={16} color={C.mid} />
          </button>
        </div>

        {/* Invoice list */}
        <div style={{ overflowY: 'auto', flex: 1 }}>
          {filtered.length === 0 && (
            <div style={{ padding: '2rem', textAlign: 'center', color: C.faint, fontSize: '0.85rem' }}>No invoices found.</div>
          )}
          {filtered.map((inv, i) => (
            <div key={inv.InvoiceID || i}
              onClick={() => onSelectInv(inv)}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.55rem 1.1rem', borderBottom: `1px solid ${C.border}`, cursor: 'pointer', background: C.white, gap: '0.75rem' }}
              onMouseEnter={e => e.currentTarget.style.background = C.skyBg}
              onMouseLeave={e => e.currentTarget.style.background = C.white}
            >
              {/* Left: supplier + ref/date */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: '0.84rem', fontWeight: 700, color: C.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {inv.Contact?.Name || '—'}
                </div>
                <div style={{ fontSize: '0.72rem', color: C.mid, marginTop: 1 }}>
                  {inv.InvoiceNumber || inv.Reference || '—'} · {fmtDate(inv.DueDate)}
                </div>
              </div>
              {/* Right: amount + pill */}
              <div style={{ textAlign: 'right', flexShrink: 0 }}>
                <div style={{ fontSize: '0.88rem', fontWeight: 800, color: C.ink }}>{fmt(parseFloat(inv.AmountDue || 0))}</div>
                <div style={{ marginTop: 2 }}><DuePill dueDate={inv.DueDate} /></div>
              </div>
            </div>
          ))}
        </div>

        {/* Footer total */}
        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.75rem 1.1rem', borderTop: `2px solid ${C.borderMid}`, background: C.rowAlt, flexShrink: 0 }}>
          <span style={{ fontSize: '0.82rem', fontWeight: 700, color: C.ink }}>Total</span>
          <span style={{ fontSize: '0.9rem', fontWeight: 900, color: C.navy }}>{fmt(filtered.reduce((s, i) => s + parseFloat(i.AmountDue || 0), 0))}</span>
        </div>
      </div>
    </div>
  );
}

// ── Bank balance sparkline modal ──────────────────────────────────────────────
function BankBalanceModal({ bankData, onClose, onSave, onUseXero }) {
  const [data, setData]         = useState([]);
  const [loading, setLoading]   = useState(true);
  const [editing, setEditing]   = useState(false);
  const [input,   setInput]     = useState('');
  const [saving,  setSaving]    = useState(false);

  useEffect(() => {
    const now  = new Date();
    const keys = Array.from({length:12},(_,i) => {
      const d = new Date(now.getFullYear(), now.getMonth() - 11 + i, 1);
      return { key: `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`, label: d.toLocaleString('en-NL',{month:'short'}) };
    });
    supabase.from('xero_bank_cache').select('period_key, balance').in('period_key', keys.map(k=>k.key))
      .then(({ data: rows }) => {
        const map = {};
        (rows||[]).forEach(r => { map[r.period_key] = r.balance; });
        setData(keys.map(k => ({ label: k.label, balance: map[k.key] ?? null })));
        setLoading(false);
      });
  }, []);

  async function handleSave() {
    const val = parseFloat(input.replace(',', '.'));
    if (isNaN(val)) return;
    setSaving(true);
    await onSave(val);
    setSaving(false);
    setEditing(false);
    setInput('');
  }

  async function handleUseXero() {
    setSaving(true);
    await onUseXero();
    setSaving(false);
  }

  const isManual = bankData?.source === 'manual';
  const currentBalance = bankData?.value;

  const vals  = data.filter(d=>d.balance!==null).map(d=>d.balance);
  const yMin  = vals.length ? Math.min(...vals) : 0;
  const yMax  = vals.length ? Math.max(...vals) : 1;
  const yPad  = (yMax - yMin) * 0.15 || 5000;
  const yLo   = yMin - yPad, yHi = yMax + yPad, ySpan = yHi - yLo;
  const W=320, H=120, PL=48, PR=12, PT=12, PB=24;
  const cW=W-PL-PR, cH=H-PT-PB;
  const n = data.length;
  const xPos = i => PL + (i/Math.max(n-1,1))*cW;
  const yPos = v => PT + (1-(v-yLo)/ySpan)*cH;
  const fmtY = v => { const a=Math.abs(v); return a>=1000?`€${v<0?'-':''}${Math.round(a/1000)}k`:`€${Math.round(v)}`; };
  const points = data.map((d,i) => d.balance!==null ? `${xPos(i).toFixed(1)},${yPos(d.balance).toFixed(1)}` : null).filter(Boolean);

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(15,23,42,0.55)', zIndex:1000, display:'flex', alignItems:'flex-end', justifyContent:'center' }}
      onClick={onClose}>
      <div style={{ background:C.white, borderRadius:'16px 16px 0 0', width:'100%', maxWidth:580, paddingBottom:'calc(1.5rem + env(safe-area-inset-bottom))' }}
        onClick={e=>e.stopPropagation()}>
        <div style={{ width:36, height:4, borderRadius:2, background:C.borderMid, margin:'0.85rem auto 0' }} />
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'0.85rem 1.1rem 0.65rem', borderBottom:`1px solid ${C.border}` }}>
          <div>
            <div style={{ fontWeight:800, fontSize:'0.95rem', color:C.ink }}>Bank Balance</div>
            <div style={{ fontSize:'0.75rem', color:C.mid, marginTop:2 }}>
              {isManual ? 'Manual entry · month-end chart from Xero cache' : 'Xero book balance · month-end chart'}
            </div>
          </div>
          <div style={{ display:'flex', alignItems:'center', gap:'0.75rem' }}>
            <div style={{ fontSize:'1.1rem', fontWeight:900, color:C.skyText }}>{currentBalance != null ? fmt(currentBalance) : '—'}</div>
            <button onClick={onClose} style={{ border:'none', background:C.rowAlt, borderRadius:8, padding:'0.35rem', cursor:'pointer', display:'flex', alignItems:'center' }}>
              <X size={16} color={C.mid} />
            </button>
          </div>
        </div>

        {/* Manual balance controls */}
        <div style={{ padding:'0.75rem 1.1rem', borderBottom:`1px solid ${C.border}`, display:'flex', alignItems:'center', gap:'0.6rem', flexWrap:'wrap' }}>
          {!editing ? (
            <>
              <button onClick={() => { setEditing(true); setInput(isManual && currentBalance ? String(currentBalance) : ''); }}
                style={{ padding:'0.35rem 0.75rem', borderRadius:7, border:`1px solid ${C.border}`, background:C.white, color:C.navy, fontSize:'0.78rem', fontWeight:700, cursor:'pointer' }}>
                ✏️ Enter balance
              </button>
              {isManual && (
                <button onClick={handleUseXero} disabled={saving}
                  style={{ padding:'0.35rem 0.75rem', borderRadius:7, border:`1px solid ${C.border}`, background:C.white, color:C.mid, fontSize:'0.78rem', fontWeight:600, cursor:'pointer' }}>
                  Use Xero ({bankData?.xeroValue != null ? fmt(bankData.xeroValue) : '—'}) · reconciled
                </button>
              )}
            </>
          ) : (
            <>
              <span style={{ fontSize:'0.82rem', color:C.mid, fontWeight:600 }}>€</span>
              <input
                autoFocus
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') handleSave(); if (e.key === 'Escape') setEditing(false); }}
                placeholder="0.00"
                style={{ width:110, padding:'0.35rem 0.6rem', borderRadius:7, border:`1.5px solid ${C.navy}`, fontSize:'0.88rem', fontWeight:700, color:C.ink, outline:'none' }}
              />
              <button onClick={handleSave} disabled={saving}
                style={{ padding:'0.35rem 0.75rem', borderRadius:7, border:'none', background:C.navy, color:C.white, fontSize:'0.78rem', fontWeight:700, cursor:'pointer' }}>
                {saving ? 'Saving…' : 'Save'}
              </button>
              <button onClick={() => setEditing(false)}
                style={{ padding:'0.35rem 0.6rem', borderRadius:7, border:`1px solid ${C.border}`, background:C.white, color:C.mid, fontSize:'0.78rem', cursor:'pointer' }}>
                Cancel
              </button>
            </>
          )}
        </div>
        <div style={{ padding:'1rem 1.1rem 0' }}>
          {loading ? <Spinner label="Loading…" /> : (
            <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{ display:'block' }}>
              {/* Y gridlines */}
              {[0,0.5,1].map((t,i) => {
                const v = yLo + t*ySpan;
                const y = yPos(v);
                return (
                  <g key={i}>
                    <line x1={PL} y1={y} x2={PL+cW} y2={y} stroke={C.border} strokeWidth={0.5}/>
                    <text x={PL-4} y={y+3} textAnchor="end" fontSize={8} fill={C.mid}>{fmtY(v)}</text>
                  </g>
                );
              })}
              {/* Line */}
              {points.length > 1 && (
                <polyline points={points.join(' ')} fill="none" stroke={C.skyText} strokeWidth={2} strokeLinejoin="round"/>
              )}
              {/* Dots + x labels */}
              {data.map((d,i) => (
                <g key={i}>
                  {d.balance!==null && <circle cx={xPos(i)} cy={yPos(d.balance)} r={3} fill={C.skyText} stroke={C.white} strokeWidth={1.5}/>}
                  <text x={xPos(i)} y={H-4} textAnchor="middle" fontSize={8} fill={C.mid}>{d.label}</text>
                </g>
              ))}
            </svg>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Supplier Summary — landing state before a supplier is selected ─────────────
function SupplierSummary({ suppliers, onSelect, isMobile, liveBalances = {}, bankData, openReceivables, onTileClick, onBankSave, onBankUseXero, onBankClear }) {
  const getOutstanding = (c) => liveBalances[c.ContactID]?.outstanding ?? c.Balances?.AccountsPayable?.Outstanding ?? 0;
  const getOverdue     = (c) => liveBalances[c.ContactID]?.overdue     ?? c.Balances?.AccountsPayable?.Overdue     ?? 0;
  const hasLive        = Object.keys(liveBalances).length > 0;

  const totalOutstanding = suppliers.reduce((s, c) => s + getOutstanding(c), 0);
  const totalOverdue     = suppliers.reduce((s, c) => s + getOverdue(c), 0);
  const overdueCount     = suppliers.filter(c => getOverdue(c) > 0).length;

  const overdueList = suppliers
    .filter(c => getOverdue(c) > 0)
    .sort((a, b) => getOverdue(b) - getOverdue(a));

  // null = still loading, undefined = failed/no data
  const bankLoading = bankData === null;
  const recLoading  = openReceivables === null;

  // Derive bank tile display values
  function bankSub() {
    if (bankLoading) return 'Loading…';
    if (!bankData)   return '—';
    if (bankData.source === 'xero') return 'Xero book balance';
    if (!bankData.updatedAt) return 'Manual entry';
    const updated  = new Date(bankData.updatedAt);
    const now      = new Date();
    const diffMins = Math.round((now - updated) / 60000);
    const diffHrs  = Math.round(diffMins / 60);
    if (diffMins < 60)  return `Updated ${diffMins}m ago`;
    if (diffHrs  < 24)  return `Updated ${diffHrs}h ago`;
    // Stale — show date clearly so it's obvious
    return `Last updated ${updated.toLocaleDateString('en-NL', { weekday: 'short', day: 'numeric', month: 'short' })}`;
  }

  // All 5 tiles as a single unified array
  const tiles = [
    { label: 'Bank Balance', value: bankLoading ? null : bankData?.value, accent: C.skyText, bg: C.skyBg,
      sub: bankSub(), onClick: () => onTileClick('bank'), loading: bankLoading },
    { label: 'Open Sales Invoices', value: recLoading ? null : openReceivables, accent: C.green, bg: C.greenBg,
      sub: 'Outstanding receivables · live', onClick: () => onTileClick('receivables'), loading: recLoading },
    { label: 'Total Outstanding', value: totalOutstanding, onClick: () => onTileClick('outstanding'),
      accent: totalOutstanding === 0 ? C.green : totalOutstanding < 25000 ? C.green : totalOutstanding < 70000 ? C.amber : C.red,
      bg:     totalOutstanding === 0 ? C.greenBg : totalOutstanding < 25000 ? C.greenBg : totalOutstanding < 70000 ? C.amberBg : C.redBg,
      sub: totalOutstanding === 0 ? 'All clear' : 'All open invoices' },
    { label: 'Total Overdue', value: totalOverdue, onClick: () => onTileClick('overdue'),
      accent: totalOverdue === 0 ? C.green : totalOverdue < 10000 ? C.amber : C.red,
      bg:     totalOverdue === 0 ? C.greenBg : totalOverdue < 10000 ? C.amberBg : C.redBg,
      sub: totalOverdue === 0 ? 'All clear' : 'Requires attention' },
    { label: 'Suppliers Overdue', value: overdueCount,
      accent: overdueCount === 0 ? C.green : C.amber,
      bg:     overdueCount === 0 ? C.greenBg : C.amberBg,
      sub: overdueCount === 0 ? 'None overdue' : `of ${suppliers.length} suppliers` },
  ];

  // Mobile: 2 columns, hide Suppliers Overdue (last tile)
  const visibleTiles = isMobile ? tiles.slice(0, 4) : tiles;

  return (
    <div>
      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(5, 1fr)',
        gap: '0.65rem',
        marginBottom: '1.25rem',
      }}>
        {visibleTiles.map((card, i) => (
          <div key={i} onClick={card.onClick} style={{
            background: card.bg, border: `1px solid ${C.border}`,
            borderTop: `3px solid ${card.accent}`, borderRadius: 12, padding: '0.85rem 1rem',
            cursor: card.onClick ? 'pointer' : 'default',
            minWidth: 0,
          }}>
            <div style={{ fontSize: '0.67rem', color: C.mid, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{card.label}</div>
            <div style={{ fontSize: '1.15rem', fontWeight: 900, color: card.accent, letterSpacing: '-0.02em' }}>
              {card.loading ? <span style={{ color: C.faint, fontSize: '0.85rem', fontWeight: 400 }}>Loading…</span> : card.value != null ? fmt(card.value) : '—'}
            </div>
            <div style={{ fontSize: '0.7rem', color: C.mid, marginTop: 3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{card.sub}</div>
          </div>
        ))}
      </div>

      {hasLive && (
        <div style={{ fontSize: '0.7rem', color: C.green, marginBottom: '0.75rem', fontWeight: 600 }}>
          ● Live from Xero
        </div>
      )}

      {overdueList.length > 0 && (
        <div style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: 12, overflow: 'hidden' }}>
          <div style={{ padding: '0.85rem 1.1rem', borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ fontSize: '0.72rem', fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Overdue Suppliers</div>
            <div style={{ fontSize: '0.72rem', color: C.faint }}>Click to view invoices</div>
          </div>
          {overdueList.map((s, i) => {
            const overdue     = getOverdue(s);
            const outstanding = getOutstanding(s);
            const pct         = totalOverdue > 0 ? (overdue / totalOverdue) * 100 : 0;
            return (
              <div key={s.ContactID}
                onClick={() => onSelect(s)}
                style={{
                  display: 'flex', alignItems: 'center', gap: '0.75rem',
                  padding: '0.8rem 1.1rem',
                  borderBottom: i < overdueList.length - 1 ? `1px solid ${C.border}` : 'none',
                  cursor: 'pointer',
                  background: i % 2 === 0 ? C.white : C.sectionBg,
                }}
                onMouseEnter={e => e.currentTarget.style.background = C.skyBg}
                onMouseLeave={e => e.currentTarget.style.background = i % 2 === 0 ? C.white : C.sectionBg}
              >
                <div style={{ fontSize: '0.75rem', fontWeight: 700, color: C.faint, width: 18, flexShrink: 0, textAlign: 'right' }}>{i + 1}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '0.85rem', fontWeight: 700, color: C.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.Name}</div>
                  <div style={{ marginTop: 5, height: 4, borderRadius: 2, background: C.border, overflow: 'hidden' }}>
                    <div style={{ height: '100%', borderRadius: 2, background: C.amber, width: `${Math.min(pct, 100)}%`, transition: 'width 0.3s ease' }} />
                  </div>
                </div>
                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                  <div style={{ fontSize: '0.9rem', fontWeight: 800, color: C.amber }}>{fmt(overdue)}</div>
                  {outstanding > overdue && (
                    <div style={{ fontSize: '0.72rem', color: C.mid, marginTop: 2 }}>{fmt(outstanding)} outstanding</div>
                  )}
                </div>
              </div>
            );
          })}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.75rem 1.1rem', background: C.rowAlt, borderTop: `2px solid ${C.borderMid}` }}>
            <span style={{ fontSize: '0.8rem', fontWeight: 700, color: C.ink }}>Total Overdue</span>
            <span style={{ fontSize: '0.9rem', fontWeight: 900, color: C.amber }}>{fmt(totalOverdue)}</span>
          </div>
        </div>
      )}

      {overdueList.length === 0 && (
        <div style={{ textAlign: 'center', color: C.green, padding: '2rem 1rem', fontSize: '0.88rem', fontWeight: 700 }}>
          ✓ No overdue invoices
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// SUPPLIER SPEND TAB
// ══════════════════════════════════════════════════════════════════════════════
export default function SupplierSpendTab({ role }) {
  const isMobile = useIsMobile();
  const [suppliers, setSuppliers]       = useState([]);
  const [suppLoading, setSuppLoading]   = useState(true);
  const [suppError, setSuppError]       = useState(null);
  const [selected, setSelected]         = useState(null);
  const [spendData, setSpendData]       = useState(null);
  const [spendLoading, setSpendLoading] = useState(false);
  const [spendError, setSpendError]     = useState(null);
  const [codeFilter, setCodeFilter]     = useState('all');
  const [modalInv, setModalInv]         = useState(null);
  const [liveBalances, setLiveBalances]         = useState({});
  const [bankData, setBankData]                 = useState(null);   // null = loading, {value, source, updatedAt, xeroValue}
  const [openReceivables, setOpenReceivables]   = useState(null);   // null = loading
  const [payablesInvoices, setPayablesInvoices] = useState([]);
  const [receivablesInvoices, setReceivablesInvoices] = useState([]);
  const [tileModal, setTileModal]               = useState(null);   // 'outstanding'|'overdue'|'receivables'|'bank'

  useEffect(() => {
    // Load contacts from cache for the supplier list
    supabase
      .from('xero_contacts_cache')
      .select('contact_id, contact_name, account_number, outstanding, overdue, admin_only, raw')
      .eq('show_in_lemon_tree', true)
      .then(({ data, error }) => {
        if (error) { setSuppError(error.message); setSuppLoading(false); return; }
        if (!data?.length) { setSuppError('No suppliers cached yet — run a sync from Data Sync first.'); setSuppLoading(false); return; }
        const list = data
          .map(r => ({
            ContactID:     r.contact_id,
            Name:          r.contact_name,
            AccountNumber: r.account_number,
            admin_only:    r.admin_only,
            Balances:      { AccountsPayable: { Outstanding: r.outstanding, Overdue: r.overdue } },
            ...r.raw,
          }))
          .filter(c => !isSupplierHidden(c, role))
          .sort((a, b) => a.Name.localeCompare(b.Name));
        setSuppliers(list);
        setSuppLoading(false);
      });

    // Load manual balance from Supabase, then fetch Xero book balance
    const loadBankData = async () => {
      // Fetch Xero book balance (Closing Balance = index 4)
      let xeroValue = null;
      try {
        const toDate = new Date().toISOString().split('T')[0];
        const res    = await fetch(`${SUPABASE_FUNCTIONS_URL}/xero-data?type=banksummary&toDate=${toDate}`);
        const data   = await res.json();
        const rows   = data?.Reports?.[0]?.Rows || [];
        let total = 0;
        rows.forEach(section => {
          (section.Rows || []).forEach(row => {
            if (row.RowType === 'Row') {
              const cells = row.Cells || [];
              // Closing Balance is index 4 per confirmed report structure
              const val = parseFloat(cells[4]?.Value || '0');
              if (!isNaN(val)) total += val;
            }
          });
        });
        xeroValue = total || null;
      } catch(e) { console.error('[banksummary] fetch failed:', e); }

      // Check for manual balance in Supabase
      const { data: manual } = await supabase
        .from('manual_balances')
        .select('value, updated_at, source')
        .eq('key', 'revolut_eur_main')
        .single();

      if (manual) {
        setBankData({ value: manual.value, source: manual.source, updatedAt: manual.updated_at, xeroValue });
      } else {
        // No manual entry — use Xero
        setBankData({ value: xeroValue, source: 'xero', updatedAt: null, xeroValue });
      }
    };
    loadBankData();

    // Live open payables — per-supplier outstanding/overdue for landing list
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    fetch(`${SUPABASE_FUNCTIONS_URL}/xero-data?type=all-payables`)
      .then(r => r.json())
      .then(data => {
        const balances = {};
        (data.Invoices || []).forEach(inv => {
          const cid = inv.Contact?.ContactID;
          if (!cid || inv.Type !== 'ACCPAY' || inv.Status !== 'AUTHORISED') return;
          const amt = parseFloat(inv.AmountDue || 0);
          if (amt <= 0) return;
          if (!balances[cid]) balances[cid] = { outstanding: 0, overdue: 0 };
          balances[cid].outstanding += amt;
          const match = (inv.DueDate || '').match(/(\d+)/);
          if (match) {
            const due = new Date(parseInt(match[1]));
            if (due < today) balances[cid].overdue += amt;
          }
        });
        setLiveBalances(balances);
        // Store full invoice list for modal
        setPayablesInvoices(data.Invoices || []);
      })
      .catch(err => console.error('[all-payables] fetch failed:', err));

    // Live open receivables — total money owed to us
    fetch(`${SUPABASE_FUNCTIONS_URL}/xero-data?type=open-receivables`)
      .then(r => r.json())
      .then(data => {
        const invoices = data.Invoices || [];
        const total = invoices.reduce((s, inv) => s + parseFloat(inv.AmountDue || 0), 0);
        setOpenReceivables(total);
        setReceivablesInvoices(invoices);
      })
      .catch(err => { console.error('[open-receivables] fetch failed:', err); setOpenReceivables(undefined); });
  }, [role]); // eslint-disable-line

  const loadSpend = useCallback(async (contact) => {
    if (!contact) { setSelected(null); setSpendData(null); return; }
    setSelected(contact); setSpendData(null); setSpendError(null); setSpendLoading(true); setCodeFilter('all');
    const months = getLast12Months();
    try {
      const [invoiceRes, bankTxRes] = await Promise.all([
        fetch(`${SUPABASE_FUNCTIONS_URL}/xero-data?type=supplier-spend&contactId=${contact.ContactID}&fromDate=${months[0].from}&toDate=${months[months.length - 1].to}`),
        fetch(`${SUPABASE_FUNCTIONS_URL}/xero-data?type=bank-transactions&contactId=${contact.ContactID}&fromDate=${months[0].from}&toDate=${months[months.length - 1].to}`),
      ]);
      const [invoiceData, bankTxData] = await Promise.all([invoiceRes.json(), bankTxRes.json()]);

      // Normalise BankTransactions (SPEND type) to invoice shape
      const spendMoney = (bankTxData.BankTransactions || [])
        .filter(tx => tx.Type === 'SPEND' && tx.Status === 'AUTHORISED'
          && tx.Contact?.ContactID === contact.ContactID)
        .map(tx => ({
          InvoiceID:     tx.BankTransactionID,
          Type:          'SPEND',
          Status:        'AUTHORISED',
          Date:          tx.Date,
          DueDate:       null,
          SubTotal:      tx.Total || tx.SubTotal || 0,
          InvoiceNumber: tx.Reference || '—',
          Reference:     tx.Reference || '—',
          LineItems:     tx.LineItems || [],
          Contact:       tx.Contact,
          _isSpendMoney: true,
        }));

      const allInvoices = [
        ...(invoiceData.Invoices || []).filter(i => ['AUTHORISED', 'PAID', 'DRAFT'].includes(i.Status)),
        ...spendMoney,
      ].sort((a, b) => (parseXeroDate(b.Date)?.getTime() || 0) - (parseXeroDate(a.Date)?.getTime() || 0));

      const purchaseInvoices = allInvoices.filter(i => i.Type === 'ACCPAY' || i.Type === 'SPEND');
      const salesInvoices    = allInvoices.filter(i => i.Type === 'ACCREC');

      const monthlyMap = {};
      months.forEach(m => { monthlyMap[m.key] = { label: m.label, total: 0 }; });
      const accountMap = {};
      let totalDays = 0, paidCount = 0;

      purchaseInvoices.forEach(inv => {
        const mk = mKey(inv.Date);
        if (mk && monthlyMap[mk] && inv.Status !== 'DRAFT') monthlyMap[mk].total += parseFloat(inv.Total || inv.SubTotal || 0);
        if (inv.Status === 'PAID' && inv.Date && inv.FullyPaidOnDate) {
          const days = Math.round((parseXeroDate(inv.FullyPaidOnDate) - parseXeroDate(inv.Date)) / 86400000);
          if (days >= 0) { totalDays += days; paidCount++; }
        }
        (inv.LineItems || []).forEach(line => {
          const code = line.AccountCode || 'Unassigned';
          if (!accountMap[code]) accountMap[code] = { code, total: 0, name: line.AccountCode || 'Unassigned' };
          accountMap[code].total += parseFloat(line.LineAmount || 0);
        });
      });

      // ── Tile fix: derive outstanding/overdue directly from invoices in hand ──
      // Outstanding = open ACCPAY invoices with AmountDue > 0
      // Overdue     = subset where DueDate is in the past
      const today = new Date(); today.setHours(0, 0, 0, 0);
      const openInvoices = purchaseInvoices.filter(i => i.Type === 'ACCPAY' && i.Status === 'AUTHORISED');
      const outstanding = openInvoices.reduce((s, i) => s + parseFloat(i.AmountDue || 0), 0);
      const overdue = openInvoices.filter(i => {
        const due = parseXeroDate(i.DueDate);
        return due && due < today;
      }).reduce((s, i) => s + parseFloat(i.AmountDue || 0), 0);

      // Log for verification — remove once confirmed accurate
      console.log(`[${contact.Name}] open invoices: ${openInvoices.length}, outstanding: ${outstanding}, overdue: ${overdue}`);

      const totalIncome = salesInvoices.reduce((s, i) => s + parseFloat(i.Total || i.SubTotal || 0), 0);

      setSpendData({
        chartData:     Object.values(monthlyMap),
        totalSpend:    Object.values(monthlyMap).reduce((s, m) => s + m.total, 0),
        totalIncome,
        hasIncome:     salesInvoices.length > 0,
        outstanding,
        overdue,
        avgPayDays:    paidCount > 0 ? Math.round(totalDays / paidCount) : null,
        invoices:      allInvoices,
        accountCodes:  Object.values(accountMap).sort((a, b) => b.total - a.total),
        hasSpendMoney: spendMoney.length > 0,
      });
    } catch (e) { setSpendError(e.message); }
    finally { setSpendLoading(false); }
  }, []); // eslint-disable-line

  const filteredInvoices = spendData?.invoices.filter(inv => {
    if (codeFilter === 'spend') return inv._isSpendMoney === true;
    if (codeFilter !== 'all') return !inv._isSpendMoney && (inv.LineItems || []).some(l => (l.AccountCode || 'Unassigned') === codeFilter);
    return true;
  }) || [];

  async function saveBankBalance(value) {
    await supabase.from('manual_balances').upsert({
      key: 'revolut_eur_main', value, source: 'manual', updated_at: new Date().toISOString()
    }, { onConflict: 'key' });
    setBankData(prev => ({ ...prev, value, source: 'manual', updatedAt: new Date().toISOString() }));
  }

  async function useXeroBalance() {
    await supabase.from('manual_balances').upsert({
      key: 'revolut_eur_main', value: bankData?.xeroValue, source: 'xero', updated_at: new Date().toISOString()
    }, { onConflict: 'key' });
    setBankData(prev => ({ ...prev, value: prev?.xeroValue, source: 'xero' }));
  }

  return (
    <div>
      {modalInv && <InvoiceModal inv={modalInv} onClose={() => setModalInv(null)} />}

      {/* Tile modals */}
      {(tileModal === 'outstanding' || tileModal === 'overdue') && (
        <InvoiceListModal
          title={tileModal === 'overdue' ? 'Overdue Invoices' : 'Outstanding Invoices'}
          invoices={payablesInvoices}
          filter={tileModal === 'overdue' ? 'overdue' : 'all'}
          total={tileModal === 'overdue'
            ? payablesInvoices.filter(inv => { const d=parseXeroDate(inv.DueDate); const t=new Date(); t.setHours(0,0,0,0); return d&&d<t; }).reduce((s,i)=>s+parseFloat(i.AmountDue||0),0)
            : payablesInvoices.reduce((s,i)=>s+parseFloat(i.AmountDue||0),0)
          }
          onSelectInv={inv => { setTileModal(null); setModalInv(inv); }}
          onClose={() => setTileModal(null)}
        />
      )}
      {tileModal === 'receivables' && (
        <InvoiceListModal
          title="Open Sales Invoices"
          invoices={receivablesInvoices}
          filter="all"
          total={receivablesInvoices.reduce((s,i)=>s+parseFloat(i.AmountDue||0),0)}
          onSelectInv={inv => { setTileModal(null); setModalInv(inv); }}
          onClose={() => setTileModal(null)}
        />
      )}
      {tileModal === 'bank' && (
        <BankBalanceModal bankData={bankData} onClose={() => setTileModal(null)} onSave={saveBankBalance} onUseXero={useXeroBalance} />
      )}

      <div style={{ marginBottom: '1.5rem' }}>
        <div style={{ fontSize: '0.73rem', fontWeight: 700, color: C.mid, marginBottom: '0.5rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Select Supplier</div>
        {suppError && <ErrorBanner message={suppError} />}
        <SupplierSearch suppliers={suppliers} loading={suppLoading} selected={selected} onSelect={loadSpend} />
      </div>

      {spendLoading && <Spinner label={`Loading invoices for ${selected?.Name}…`} />}
      {spendError   && <ErrorBanner message={spendError} />}

      {spendData && !spendLoading && (
        <>
          <StatRow cards={[
            { label: 'Total Spend (12m)', value: fmt(spendData.totalSpend), accent: C.navy },
            ...(spendData.hasIncome ? [{
              label: 'Income Received',
              value: fmt(spendData.totalIncome),
              accent: '#6db89a',
              sub: `Net ${fmt(spendData.totalSpend - spendData.totalIncome)}`,
            }] : []),
            { label: 'Outstanding', value: fmt(spendData.outstanding), accent: spendData.outstanding > 0 ? C.amber : C.green },
            { label: 'Overdue',     value: fmt(spendData.overdue),     accent: spendData.overdue > 0 ? C.red : C.green },
            { label: 'Avg / Month', value: fmt(spendData.totalSpend / 12), accent: C.skyText },
            {
              label: 'Avg Payment Days',
              value: spendData.avgPayDays != null ? `${spendData.avgPayDays}d` : '—',
              accent: spendData.avgPayDays == null ? C.mid : spendData.avgPayDays <= 14 ? C.green : spendData.avgPayDays <= 30 ? C.amber : C.red,
              sub: spendData.avgPayDays != null ? (spendData.avgPayDays <= 14 ? 'Prompt' : spendData.avgPayDays <= 30 ? 'Within terms' : 'Slow') : 'No paid invoices',
            },
          ]} />

          {/* Chart */}
          <div style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: 12, padding: '1.1rem 1.25rem', marginBottom: '1.25rem' }}>
            <div style={{ fontSize: '0.72rem', fontWeight: 700, color: C.mid, marginBottom: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Monthly Spend — Last 12 Months</div>
            <ResponsiveContainer width="100%" height={140}>
              <BarChart data={spendData.chartData} margin={{ top: 0, right: 0, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={C.border} vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 10, fill: C.mid }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 10, fill: C.mid }} axisLine={false} tickLine={false} tickFormatter={v => `€${(v / 1000).toFixed(0)}k`} width={36} />
                <Tooltip content={<CustomTooltip />} cursor={{ fill: C.skyBg }} />
                <Bar dataKey="total" fill={C.navy} radius={[3, 3, 0, 0]} maxBarSize={32} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Invoice list */}
          <div style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: 12, overflow: 'hidden' }}>
            <div style={{ padding: '0.9rem 1.1rem', borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.5rem' }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: '0.88rem', color: C.ink }}>
                  Invoices <span style={{ color: C.mid, fontWeight: 400, fontSize: '0.8rem' }}>({filteredInvoices.length})</span>
                </div>
                {codeFilter !== 'all' && (
                  <div style={{ fontSize: '0.75rem', color: C.skyText, fontWeight: 600, marginTop: 3 }}>
                    {codeFilter === 'spend'
                      ? 'Spend Money transactions'
                      : (() => { const a = spendData.accountCodes.find(a => a.code === codeFilter); return a?.name ? a.name : codeFilter; })()}
                  </div>
                )}
              </div>
              {spendData.accountCodes.length > 0 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', flexWrap: 'wrap' }}>
                  <Filter size={12} color={C.mid} />
                  <PillToggle
                    options={[{ key: 'all', label: 'All' }, ...spendData.accountCodes.map(a => ({ key: a.code, label: a.code }))]}
                    value={codeFilter}
                    onChange={setCodeFilter}
                    accent={C.navy}
                  />
                </div>
              )}
            </div>

            {filteredInvoices.length === 0
              ? <div style={{ padding: '2.5rem', textAlign: 'center', color: C.faint, fontSize: '0.85rem' }}>No invoices found.</div>
              : isMobile
                ? (
                  <div>
                    {filteredInvoices.map((inv, i) => (
                      <div key={inv.InvoiceID} onClick={() => setModalInv(inv)}
                        style={{ display: 'flex', alignItems: 'center', padding: '0.85rem 1rem', borderBottom: `1px solid ${C.border}`, cursor: 'pointer', background: i % 2 === 0 ? C.white : C.sectionBg }}
                        onTouchStart={e => e.currentTarget.style.background = C.skyBg}
                        onTouchEnd={e => e.currentTarget.style.background = i % 2 === 0 ? C.white : C.sectionBg}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: 700, fontSize: '0.85rem', color: C.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {inv.InvoiceNumber || inv.Reference || '—'}
                          </div>
                          <div style={{ fontSize: '0.75rem', color: C.mid, marginTop: 2 }}>
                            {fmtDate(inv.Date)}{inv.DueDate ? ` · due ${fmtDate(inv.DueDate)}` : ''}
                          </div>
                        </div>
                        <div style={{ textAlign: 'right', flexShrink: 0, marginLeft: '0.75rem' }}>
                          <div style={{ fontWeight: 800, fontSize: '0.9rem', color: inv.Type === 'ACCREC' ? C.green : C.ink }}>
                            {inv.Type === 'ACCREC' ? '+' : ''}{fmtFull(parseFloat(inv.Total || inv.SubTotal || 0))}
                          </div>
                          <div style={{ marginTop: 3 }}><StatusPill status={inv.Status} dueDate={inv.DueDate} invoiceType={inv.Type} /></div>
                        </div>
                      </div>
                    ))}
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.85rem 1rem', background: C.rowAlt, borderTop: `2px solid ${C.borderMid}` }}>
                      <span style={{ fontWeight: 700, fontSize: '0.85rem', color: C.ink }}>Total</span>
                      <span style={{ fontWeight: 800, fontSize: '0.9rem', color: C.navy }}>
                        {fmtFull(filteredInvoices.reduce((s, i) => s + (i.Type === 'ACCREC' ? -parseFloat(i.Total || i.SubTotal || 0) : parseFloat(i.Total || i.SubTotal || 0)), 0))}
                      </span>
                    </div>
                  </div>
                )
                : (
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.83rem' }}>
                      <thead>
                        <tr style={{ background: C.sectionBg }}>
                          {['Date', 'Month', 'Reference', 'Code', 'Type', 'Amount ex VAT', 'Due Date', 'Status'].map(h => (
                            <th key={h} style={{ padding: '0.6rem 1rem', textAlign: h === 'Amount ex VAT' ? 'right' : 'left', color: C.mid, fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {filteredInvoices.map((inv, i) => {
                          const codes = [...new Set((inv.LineItems || []).map(l => l.AccountCode || 'Unassigned'))].join(', ');
                          const mk2 = mKey(inv.Date);
                          const ml = mk2 ? new Date(mk2 + '-01').toLocaleString('en-NL', { month: 'short', year: '2-digit' }) : '—';
                          return (
                            <tr key={inv.InvoiceID}
                              style={{ borderTop: `1px solid ${C.border}`, background: i % 2 === 0 ? C.white : C.sectionBg, cursor: 'pointer' }}
                              onClick={() => setModalInv(inv)}
                              onMouseEnter={e => e.currentTarget.style.background = C.skyBg}
                              onMouseLeave={e => e.currentTarget.style.background = i % 2 === 0 ? C.white : C.sectionBg}>
                              <td style={{ padding: '0.6rem 1rem', color: C.ink, whiteSpace: 'nowrap' }}>{fmtDate(inv.Date)}</td>
                              <td style={{ padding: '0.6rem 1rem', color: C.mid, whiteSpace: 'nowrap', fontWeight: 600 }}>{ml}</td>
                              <td style={{ padding: '0.6rem 1rem', color: C.ink }}>{inv.InvoiceNumber || inv.Reference || '—'}</td>
                              <td style={{ padding: '0.6rem 1rem', color: C.mid, fontSize: '0.78rem' }}>{codes || '—'}</td>
                              <td style={{ padding: '0.6rem 1rem' }}>
                                <span style={{ background: inv.Type === 'ACCREC' ? '#e8f5ef' : C.navyLight, color: inv.Type === 'ACCREC' ? '#3a7d5e' : C.navy, borderRadius: 5, padding: '0.15rem 0.45rem', fontSize: '0.7rem', fontWeight: 700 }}>
                                  {inv.Type === 'ACCREC' ? 'Income' : inv.Type === 'SPEND' ? 'Spend Money' : 'Purchase'}
                                </span>
                              </td>
                              <td style={{ padding: '0.6rem 1rem', textAlign: 'right', fontWeight: 700, color: inv.Type === 'ACCREC' ? C.green : C.ink, whiteSpace: 'nowrap' }}>
                                {inv.Type === 'ACCREC' ? '+' : ''}{fmtFull(parseFloat(inv.Total || inv.SubTotal || 0))}
                              </td>
                              <td style={{ padding: '0.6rem 1rem', color: C.mid, whiteSpace: 'nowrap' }}>{fmtDate(inv.DueDate)}</td>
                              <td style={{ padding: '0.6rem 1rem' }}><StatusPill status={inv.Status} dueDate={inv.DueDate} invoiceType={inv.Type} /></td>
                            </tr>
                          );
                        })}
                      </tbody>
                      <tfoot>
                        <tr style={{ borderTop: `2px solid ${C.borderMid}`, background: C.rowAlt }}>
                          <td colSpan={5} style={{ padding: '0.65rem 1rem', fontWeight: 700, color: C.ink }}>Net Total</td>
                          <td style={{ padding: '0.65rem 1rem', textAlign: 'right', fontWeight: 800, color: C.navy }}>
                            {fmtFull(filteredInvoices.reduce((s, i) => s + (i.Type === 'ACCREC' ? -parseFloat(i.Total || i.SubTotal || 0) : parseFloat(i.Total || i.SubTotal || 0)), 0))}
                          </td>
                          <td colSpan={2} />
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                )
            }
          </div>
        </>
      )}

      {!selected && !spendLoading && suppliers.length > 0 && (
        <SupplierSummary suppliers={suppliers} onSelect={loadSpend} isMobile={isMobile} liveBalances={liveBalances} bankData={bankData} openReceivables={openReceivables} onTileClick={setTileModal} onBankSave={saveBankBalance} onBankUseXero={useXeroBalance} />
      )}
    </div>
  );
}